"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, Wrench, CircleCheck as CheckCircle2, ArrowRight, TriangleAlert as AlertTriangle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function PipeHero() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = [{
    text: "24/7 Rapid Emergency Response"
  }, {
    text: "Licensed & Insured Master Plumbers"
  }, {
    text: "Upfront Pricing With No Hidden Fees"
  }, {
    text: "Same-Day Burst & Leak Repairs"
  }];
  const quickStats = [{
    label: "Average Response Time",
    value: "< 45 Mins"
  }, {
    label: "Pipes Repaired & Restored",
    value: "3,500+"
  }, {
    label: "Customer Satisfaction",
    value: "99.4%"
  }];
  return <section id="piperepairhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Background Decorative Accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 -z-10 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-10 -z-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Content Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6 text-left">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wide gap-1.5 bg-secondary text-secondary-foreground border border-border">
                <AlertTriangle className="size-3.5 text-accent" />
                Emergency Pipe Repair in Jonesboro, GA
              </Badge>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Clock className="size-3.5 text-primary" />
                Available 24 Hours / 7 Days
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
                Burst or Leaking Pipes? Get Immediate Repairs Before Water Damage Spreads.
              </h1>
              <p className="font-sans text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Jonesboro Emergency Plumber provides prompt, pinpoint pipe leak detection, burst line patching, copper repiping, and sewer line repairs across Clayton County. We stop the water, fix the break, and protect your home.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <Button asChild size="lg" className="h-12 px-6 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                <a href="tel:4708479076" aria-label="Call emergency plumbing line now">
                  <PhoneCall className="size-5 mr-1" />
                  Call Now: (470) 847-9076
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base font-medium border-border hover:bg-muted text-foreground">
                <Link to="/contact/">
                  Request Service Online
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>

            {/* Trust Checklist */}
            <div className="pt-4 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {trustPoints.map((item, i) => <div key={item.text} data-index={i} className="flex items-center gap-2 text-sm text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>{item.text}</span>
                  </div>)}
              </div>
            </div>
          </motion.div>

          {/* Media / Visual Card Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-card">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service_technician.png-wvc-sizes" alt="Plumber diagnosing and repairing damaged copper water pipe in residential basement" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />

                {/* Floating badge inside image */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2 rounded-lg bg-background/95 px-3 py-1.5 text-xs font-semibold text-foreground shadow-sm backdrop-blur-xs">
                    <Wrench className="size-3.5 text-primary" />
                    <span>Trenchless & Traditional Pipe Solutions</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1.5 text-xs font-bold text-accent-foreground shadow-xs">
                    <ShieldCheck className="size-3.5" />
                    <span>Guaranteed Work</span>
                  </div>
                </div>
              </div>

              {/* Stat Strip */}
              <CardContent className="p-4 sm:p-5 bg-card">
                <div className="grid grid-cols-3 divide-x divide-border text-center">
                  {quickStats.map((stat, i) => <div key={stat.label} data-index={i} className="px-2 first:pl-0 last:pr-0">
                      <div className="text-lg sm:text-xl font-bold text-foreground">
                        {stat.value}
                      </div>
                      <div className="text-2xs sm:text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {stat.label}
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </div>

            {/* Floating urgent callout */}
            <Card className="hidden sm:block absolute -bottom-5 -left-5 bg-background border border-border shadow-md py-3 px-4 max-w-xs rounded-xl">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                  <Clock className="size-4 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    Active Water Leak?
                  </p>
                  <p className="text-2xs text-muted-foreground">
                    Shut off main valve & call our dispatch instantly.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}